import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FiRefreshCw, FiMail, FiUser } from "react-icons/fi";
import { Button, DataTable, PersonCell, Tabs } from "../common";
import useAsync from "../../hooks/useAsync";
import { listInvitations, resendInvitation } from "../../services/invitationApi";
import { getErrorMessage } from "../../utils/errors";
import { formatDateTime } from "../../utils/format";
import BulkResendModal from "./BulkResendModal";
import BulkSelectionBar from "./BulkSelectionBar";

// [NEW] Status views. "failed" is the e-mail that could not be delivered; "expired" is a link that ran out.
export const INVITATION_FILTERS = [
  { key: "all", label: "All", match: () => true },
  { key: "failed", label: "Failed", match: (r) => r.status === "DELIVERY_FAILED" },
  { key: "expired", label: "Expired", match: (r) => r.status === "EXPIRED" },
  { key: "sent", label: "Sending / sent", match: (r) => r.status === "PENDING_DELIVERY" || r.status === "SENT" },
  { key: "accepted", label: "Accepted", match: (r) => r.status === "ACCEPTED" },
];

// source: optional useAsync result of listInvitations() owned by the page (so the page can show counts too).
// filter / onFilterChange: optional, to let the page open the table on "failed".
export default function InvitationsTable({ version = 0, pageSize = 8, source, filter, onFilterChange }) {
  const own = useAsync(() => listInvitations(), [version], { skip: Boolean(source) });
  const list = source || own;
  const [busyId, setBusyId] = useState(null);
  const [innerFilter, setInnerFilter] = useState("all");
  const active = filter ?? innerFilter;
  const setActive = (k) => { setInnerFilter(k); onFilterChange?.(k); setSelected(new Set()); };
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  const all = useMemo(() => list.data || [], [list.data]); // [FIX] stable list (lint: react-hooks/exhaustive-deps)
  const counts = useMemo(() => Object.fromEntries(INVITATION_FILTERS.map((f) => [f.key, all.filter(f.match).length])), [all]);
  const current = INVITATION_FILTERS.find((f) => f.key === active) || INVITATION_FILTERS[0];
  const rows = useMemo(() => all.filter(current.match), [all, current]);
  const canResendRow = (r) => r.canResend;
  const selectedRows = useMemo(() => rows.filter((r) => canResendRow(r) && selected.has(r.id)), [rows, selected]);

  const resend = async (row) => {
    setBusyId(row.employeeId);
    try {
      await resendInvitation(row.employeeId);
      toast.success(`A new link is on its way to ${row.email}. Earlier links no longer work.`);
      list.reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="invite-toolbar">
        <Tabs
          value={current.key}
          onChange={setActive}
          tabs={INVITATION_FILTERS.map((f) => ({ key: f.key, label: f.label, count: counts[f.key] }))}
        />
      </div>
      <BulkSelectionBar
        count={selectedRows.length}
        actionLabel="Resend selected"
        actionIcon={FiRefreshCw}
        onClear={() => setSelected(new Set())}
        onInvite={() => setBulkOpen(true)}
      />
      <DataTable
        dense
        label="Login invitations"
        rows={rows}
        loading={list.loading && !list.data}
        pageSize={pageSize}
        selection={{ selected, onChange: setSelected, isSelectable: canResendRow, label: `${current.key === "all" ? "" : `${current.label.toLowerCase()} `}invitations` }}
        emptyTitle={list.error ? "Invitations not available" : current.key === "failed" ? "No failed invitations" : "No invitations here"}
        emptyText={list.error || (current.key === "all" ? "Use Invite on an employee without a login." : "Nothing matches this view.")}
        columns={[
          { key: "employee", label: "Employee", render: (r) => <PersonCell name={r.employeeName} meta={
            <span className="invite-meta">
              {r.username && <span className="invite-meta-line"><FiUser aria-hidden="true" />{r.username}</span>}
              {r.email && <span className="invite-meta-line"><FiMail aria-hidden="true" />{r.email}</span>}
            </span>
          } /> },
          {
            key: "status", label: "Status",
            render: (r) => (
              <span className="cell-stack">
                <span className={`badge badge-${r.tone}`}><span className="badge-dot" />{r.statusLabel}</span>
                {r.status === "PENDING_DELIVERY" && r.attempts > 0 && <small className="muted">Retrying ({r.attempts} attempt{r.attempts === 1 ? "" : "s"})</small>}
                {r.lastError && r.status !== "ACCEPTED" && <small className="muted">{r.lastError}</small>}
              </span>
            ),
          },
          { key: "sent", label: "Sent", render: (r) => (r.sentAt ? formatDateTime(r.sentAt) : "—") },
          { key: "expires", label: "Link valid until", render: (r) => (r.acceptedAt ? `Accepted ${formatDateTime(r.acceptedAt)}` : formatDateTime(r.expiresAt)) },
          {
            key: "act", label: "", align: "right",
            render: (r) => r.canResend && (
              <Button size="sm" variant="ghost" icon={FiRefreshCw} loading={busyId === r.employeeId} onClick={() => resend(r)}>Resend</Button>
            ),
          },
        ]}
      />
      <BulkResendModal
        rows={bulkOpen ? selectedRows : null}
        onClose={() => setBulkOpen(false)}
        onFinished={() => { setSelected(new Set()); list.reload(); }}
      />
    </>
  );
}
